import { useEffect, useRef, useState } from 'react';
import { Button, InputNumber, Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { normLocation, type TravelKmLocation } from '@salaries/shared';
import { getClassCatalog } from '../../api/classCatalog';

// Editor for the per-staff per-km travel distances: one row per location with the
// coach's ONE-WAY home→business distance. The engine pays, per configured
// location, rate × (distance × 2 round trip) × the coach's distinct meeting-days
// at that location. Locations are the box's Arbox locations (same catalog the
// class-bonus + P&L features use); each location appears at most once.

export function TravelKmLocationsFields({
  value,
  onChange,
}: {
  value: TravelKmLocation[];
  onChange: (v: TravelKmLocation[]) => void;
}) {
  const catalogQ = useQuery({ queryKey: ['class-catalog'], queryFn: getClassCatalog });

  // Local rows are the edit source of truth (stable focus while typing); re-seed
  // only when `value` changes from the outside (server hydration), not our own.
  const [rows, setRows] = useState<TravelKmLocation[]>(() => value);
  const emitted = useRef<TravelKmLocation[] | null>(null);
  useEffect(() => {
    if (value === emitted.current) return;
    setRows(value);
  }, [value]);

  const commit = (next: TravelKmLocation[]) => {
    setRows(next);
    emitted.current = next;
    onChange(next);
  };

  // Top "add" row: pick a location + a distance.
  const [loc, setLoc] = useState<string | undefined>();
  const [dist, setDist] = useState<number>(0);
  const taken = new Set(rows.map((r) => normLocation(r.location)));
  const canAdd = !!loc && dist > 0 && !taken.has(normLocation(loc));

  const add = () => {
    if (!canAdd) return;
    commit([...rows, { location: loc!, distance_km: dist }]);
    setLoc(undefined);
    setDist(0);
  };

  const patch = (i: number, p: Partial<TravelKmLocation>) =>
    commit(rows.map((r, idx) => (idx === i ? { ...r, ...p } : r)));
  const remove = (i: number) => commit(rows.filter((_, idx) => idx !== i));

  // Locations not already used (plus the row's own current value stays selectable).
  const locOptions = (catalogQ.data?.locations ?? []).map((l) => ({
    value: l,
    label: l,
    disabled: taken.has(normLocation(l)),
  }));

  return (
    <div>
      <div className="flex items-end gap-2 mb-3 flex-wrap">
        <Select
          showSearch
          placeholder="סניף"
          style={{ minWidth: 180 }}
          value={loc}
          onChange={setLoc}
          options={locOptions}
          optionFilterProp="label"
          loading={catalogQ.isLoading}
        />
        <InputNumber
          min={0}
          suffix='ק"מ'
          placeholder="מרחק (כיוון אחד)"
          value={dist || undefined}
          onChange={(v) => setDist(Number(v) || 0)}
          dir="ltr"
          style={{ width: 160 }}
        />
        <Button type="primary" disabled={!canAdd} onClick={add}>
          הוסף
        </Button>
      </div>

      {rows.length > 0 && (
        <div className="border rounded">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 border-b last:border-b-0 flex-wrap">
              <Select
                showSearch
                placeholder="סניף"
                style={{ minWidth: 180 }}
                value={r.location || undefined}
                onChange={(v) => patch(i, { location: v })}
                options={(catalogQ.data?.locations ?? []).map((l) => ({
                  value: l,
                  label: l,
                  disabled: l !== r.location && taken.has(normLocation(l)),
                }))}
                optionFilterProp="label"
              />
              <InputNumber
                min={0}
                suffix='ק"מ'
                value={r.distance_km || undefined}
                onChange={(v) => patch(i, { distance_km: Number(v) || 0 })}
                dir="ltr"
                style={{ width: 160 }}
              />
              <span className="text-small text-secondary-text">כיוון אחד</span>
              <span className="flex-1" />
              <Button size="small" danger type="text" onClick={() => remove(i)}>
                הסר
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
